import { useQuery } from '@apollo/client'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from '@mui/material'
import React, { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { VoiceoverPanel } from '../components/VoiceoverPanel'
import {
  VOICEOVER_SHARE_QUERY,
  VoiceoverShareData,
  VoiceoverShareVars
} from '../graphql'
import { extractVoiceovers } from '../lib/extractVoiceovers'
import { slugify } from '../lib/slugify'

// Public, login-free page. Standalone layout on purpose: no sidebar, no CMS
// navigation. The token is the only thing that grants access, and the
// `voiceoverShare` resolver enforces signature + expiry server-side.
export const SharedVoiceoverView: React.FC = () => {
  // Token lives in the query string (not the path): a JWT contains dots, and a
  // dotted path segment makes static/SPA servers look for a file and 404 instead
  // of serving index.html.
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  // The editor's chosen suggestion extension travels in the link (default aac).
  const ext = searchParams.get('ext') ?? 'aac'

  const { data, loading, error } = useQuery<
    VoiceoverShareData,
    VoiceoverShareVars
  >(VOICEOVER_SHARE_QUERY, {
    variables: { token },
    skip: !token
  })

  const mode = data?.voiceoverShare
  const rows = useMemo(() => extractVoiceovers(mode, ext), [mode, ext])
  // Prefer the tour title; the mode title is often empty.
  const tourTitle = mode?.tour?.title || mode?.title || ''
  // All recordings go into one ZIP named after the tour id (used by the importer).
  const folderName = mode?.tour?.id ?? ''

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: { xs: 2, md: 4 },
        boxSizing: 'border-box'
      }}
    >
      <Typography variant="h4" sx={{ flex: 0 }}>
        Voiceovers{tourTitle ? ` – ${tourTitle}` : ''}
      </Typography>

      {folderName
        ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 0 }}>
            <Typography variant="body2" color="text.secondary">
              Alle Aufnahmen bitte in einen ZIP-Ordner mit diesem Namen legen:
            </Typography>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {folderName}.zip
            </Typography>
            <Tooltip title="Ordnername kopieren">
              <IconButton
                size="small"
                onClick={() => navigator.clipboard?.writeText(folderName)}
              >
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
          )
        : null}

      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {loading
          ? (
            <CircularProgress />
            )
          : error || !mode
            ? (
              <Paper variant="outlined" sx={{ p: 4, maxWidth: 480 }}>
                <Typography variant="h6" gutterBottom>
                  Link ungültig oder abgelaufen
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Dieser Freigabe-Link ist nicht mehr gültig. Bitte einen neuen Link
                  anfordern.
                </Typography>
              </Paper>
              )
            : (
              <VoiceoverPanel
                rows={rows}
                modeTitle={tourTitle}
                storageKey={mode.id}
                csvFileName={`voiceovers_${slugify(tourTitle || 'tour')}.csv`}
              />
              )}
      </Box>
    </Box>
  )
}
